import { createBaseTypeUserState } from '../utils'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { AaveTypeGetUserReserveIndexes } from '../../../types/lender/aave-v3-types'
import {
  LenderYieldComplete,
  LenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMarketUid } from '../../../utils/marketUid'
import { getAaveAssets } from '../../../assets'
import { rebuildAaveReserveData, usesAaveLens } from './lens'
import { isYLDR } from '@1delta/lender-registry'

export const getAaveV3UserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  metaMap?: LenderCrossPoolMeta,
): [(data: any[]) => UserData | undefined, number] => {
  // Use same asset ordering as build function
  const assetsToQuery = getAaveAssets(chainId, lender).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase()),
  )
  const withEMode = !isYLDR(lender)
  const viaLens = usesAaveLens(chainId, lender)
  const walkCalls = assetsToQuery.length + (withEMode ? 1 : 0)
  const expectedNumberOfCalls = viaLens ? 1 : walkCalls

  return [
    (raw: any[]) => {
      if (raw.length !== expectedNumberOfCalls) {
        return undefined
      }

      // One blob in, the walk's own array out — so everything below indexes the
      // same fields regardless of which path produced them.
      const data = viaLens
        ? rebuildAaveReserveData(raw[0], assetsToQuery.length, withEMode)
        : raw
      if (!data) return undefined

      // The eMode slot is a SET-LEVEL read, not a row: it selects the collateral
      // factors and liquidation thresholds every asset below is scored against.
      // Losing it is not a missing field — silently defaulting to 0 would
      // understate the LTV of an account that IS in an eMode and report it as
      // near-liquidation. Decline the whole set instead; the caller reports it
      // as an incomplete read rather than a position.
      //
      // (It must also never become `Number('0x')` — that is NaN, which survives
      // into `userConfig.selectedMode` and renders as "MODE #NaN".)
      const eModeSlot = data[walkCalls - 1]
      const rawEMode = withEMode ? Number(eModeSlot) : 0
      if (withEMode && (isFailedCall(eModeSlot) || !Number.isFinite(rawEMode))) {
        return undefined
      }
      const userEMode = rawEMode
      const lendingPositions: { [asset: string]: any } = {}

      let totalDebt24h = 0
      let totalDeposits24h = 0

      for (let i = 0; i < assetsToQuery.length; i++) {
        const asset = assetsToQuery[i]
        const key = createMarketUid(chainId, lender, asset)
        const metaEntity = metaMap?.[key]!
        const { dataForAsset, addedDebt, addedDeposits } = createAaveV3Entry(
          i,
          data,
          key,
          metaEntity,
        )
        if (!dataForAsset) continue

        totalDebt24h += addedDebt
        totalDeposits24h += addedDeposits
        lendingPositions[key] = dataForAsset
      }

      const payload = {
        chainId,
        account,
        lendingPositions,
        rewards: [],
        userEMode,
      }
      const userData = createBaseTypeUserState(
        payload,
        metaMap!,
        totalDeposits24h,
        totalDebt24h,
      )
      return userData
    },
    expectedNumberOfCalls,
  ]
}

function createAaveV3Entry(
  i: number,
  data: any[],
  key: string,
  meta: LenderYieldComplete,
) {
  const reserveData = data[i]

  // A failed read is not a zero balance. Without this the sentinel gets indexed
  // as a string (`'0x'[0]` === `'0'`), the all-zero check below never fires and
  // the market surfaces as a phantom empty position.
  if (isFailedCall(reserveData)) {
    return { dataForAsset: undefined, addedDeposits: 0, addedDebt: 0 }
  }

  const currentATokenBalanceRaw =
    reserveData[AaveTypeGetUserReserveIndexes.currentATokenBalance]?.toString()
  const currentStableDebtRaw =
    reserveData[AaveTypeGetUserReserveIndexes.currentStableDebt]?.toString()
  const currentVariableDebtRaw =
    reserveData[AaveTypeGetUserReserveIndexes.currentVariableDebt]?.toString()

  if (
    currentATokenBalanceRaw === '0' &&
    currentStableDebtRaw === '0' &&
    currentVariableDebtRaw === '0'
  ) {
    return {
      dataForAsset: undefined,
      addedDeposits: 0,
      addedDebt: 0,
    }
  }

  const assetMeta = meta.asset
  const decimals = assetMeta?.decimals ?? 18

  const currentATokenBalance = parseRawAmount(currentATokenBalanceRaw, decimals)
  const currentStableDebt = parseRawAmount(currentStableDebtRaw, decimals)
  const currentVariableDebt = parseRawAmount(currentVariableDebtRaw, decimals)

  // prices
  const price = getDisplayPrice(meta)
  const oPrice = getOraclePrice(meta)
  const priceHist = meta?.price?.priceUsd24h ?? price

  const dataForAsset = {
    marketUid: key,
    underlying: assetMeta.address,
    deposits: currentATokenBalance,
    debtStable: currentStableDebt,
    debt: currentVariableDebt,
    depositsUSD: Number(currentATokenBalance) * price,
    debtStableUSD: Number(currentStableDebt) * price,
    debtUSD: Number(currentVariableDebt) * price,
    depositsUSDOracle: Number(currentATokenBalance) * oPrice,
    debtStableUSDOracle: Number(currentStableDebt) * oPrice,
    debtUSDOracle: Number(currentVariableDebt) * oPrice,
    stableBorrowRate:
      reserveData[AaveTypeGetUserReserveIndexes.stableBorrowRate]?.toString() ??
      '0',
    collateralEnabled: Boolean(
      reserveData[AaveTypeGetUserReserveIndexes.usageAsCollateralEnabled],
    ),
    claimableRewards: 0,
  }

  return {
    dataForAsset,
    addedDeposits: Number(currentATokenBalance) * priceHist,
    addedDebt:
      (Number(currentVariableDebt) + Number(currentStableDebt)) * priceHist,
  }
}
